// AICA Theme Context
// Manages theme preferences (dark/light mode, color schemes)
// Bloomberg Terminal style: Professional, customizable, persistent

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export type Theme = 'light' | 'dark' | 'system';

export type ColorScheme = 'default' | 'blue' | 'green' | 'purple' | 'amber' | 'meridian';

export interface ThemeContextValue {
  /**
   * Current theme (light/dark/system)
   */
  theme: Theme;
  /**
   * Set theme
   */
  setTheme: (theme: Theme) => void;
  /**
   * Resolved theme (light or dark) after system preference
   */
  resolvedTheme: 'light' | 'dark';
  /**
   * Color scheme
   */
  colorScheme: ColorScheme;
  /**
   * Set color scheme
   */
  setColorScheme: (scheme: ColorScheme) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export interface ThemeProviderProps {
  children: ReactNode;
  /**
   * Default theme (default: 'system')
   */
  defaultTheme?: Theme;
  /**
   * Default color scheme (default: 'default')
   */
  defaultColorScheme?: ColorScheme;
  /**
   * Storage key for localStorage (default: 'aica-theme')
   */
  storageKey?: string;
}

/**
 * Theme Provider
 * Manages theme state and syncs with localStorage and system preferences
 */
export function ThemeProvider({
  children,
  defaultTheme = 'light',
  defaultColorScheme = 'meridian',
  storageKey = 'aica-theme',
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Load from localStorage
    const stored = localStorage.getItem(storageKey);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
    return defaultTheme;
  });

  const [colorScheme, setColorSchemeState] = useState<ColorScheme>(() => {
    const stored = localStorage.getItem(`${storageKey}-color-scheme`);
    if (
      stored === 'default' ||
      stored === 'blue' ||
      stored === 'green' ||
      stored === 'purple' ||
      stored === 'amber' ||
      stored === 'meridian'
    ) {
      return stored;
    }
    return defaultColorScheme;
  });

  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(() => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // Listen to system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Resolve theme (system -> light/dark)
  const resolvedTheme: 'light' | 'dark' = theme === 'system' ? systemTheme : theme;

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;

    // Remove previous theme classes
    root.classList.remove('light', 'dark');

    // Add current theme class
    root.classList.add(resolvedTheme);

    // Add color scheme class
    root.setAttribute('data-color-scheme', colorScheme);
  }, [resolvedTheme, colorScheme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem(storageKey, newTheme);
  };

  const setColorScheme = (newScheme: ColorScheme) => {
    setColorSchemeState(newScheme);
    localStorage.setItem(`${storageKey}-color-scheme`, newScheme);
  };

  const value: ThemeContextValue = {
    theme,
    setTheme,
    resolvedTheme,
    colorScheme,
    setColorScheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * useTheme Hook
 * Access theme context
 *
 * @example
 * ```tsx
 * const { theme, setTheme, resolvedTheme } = useTheme();
 *
 * <Button onClick={() => setTheme('dark')}>
 *   Dark Mode
 * </Button>
 * ```
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
